import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { convertSignType, repositorySign } from "../../../../_base/_const/_constVar";
import { MessageResponse } from "../../../../model/MessageResponse";
import {
  IconArrowRight,
  IconAt,
  IconBadge,
  IconBuilding,
  IconCalendar,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconClock,
  IconClockFilled,
  IconFolders,
  IconListCheck,
  IconMail,
  IconPhone,
  IconThumbUpFilled,
  IconUser,
} from "@tabler/icons-react";
import {
  formatDateTransfer,
  formatDateTransferVN,
} from "../../../../_base/extension/FormatDate";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { SelectListItem } from "../../../../model/SelectListItem";
import { Loading } from "../../../../_base/component/_layout/Loading";
import {
  getDepartmentSelect,
  getRoleSelect,
  getUsersSelect,
} from "../../../../const/getSelectApi";
import { UserModel } from "../../../../model/UserModel";
import { proxyAvatar } from "../../../../_base/helper/ImageProxy";

interface HistoryProfileProps {
  ids: number | null;
}
interface DocumentInfo {
  approvedQuan: number;
  createBy: number;
  createDate: string;
  docName: string;
  toltalQuan: number;
}
interface ApprovalStatus {
  status: string;
  empId: string;
  stepName: string;
  signType: number;
  stepNumber: number;
  processingDate: string;
  ekyc: boolean;
}

interface ApprovalStatusRes {
  document: DocumentInfo;
  listSteps: ApprovalStatus[];
}

export interface DetailApproveHistoryModel {
  setupStepId?: number;
  status?: string;
  empId?: string;
  stepName?: string;
  signType?: number;
  ekyc?: boolean;
  stepNumber?: number;
  processingDate?: Date | null;
}

// Mô hình cho DetailListSetupStepModel
export interface DetailListSetupStepModel {
  setupStepId?: number;
  docName?: string;
  createDate?: Date | null;
  createBy?: number;
  approvedQuan?: number;
  toltalQuan?: number;
  detailApproveHistoryModels?: DetailApproveHistoryModel[];
}

const HistoryProfile: React.FC<HistoryProfileProps> = ({ ids }) => {
  const [visibleHandelLoad, setVisibleHandelLoad] = useState(false);
  const [height, setHeight] = useState(0);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [dataHistoryProFileProps, setDataHistoryProFileProps] =
    useState<DetailListSetupStepModel[]>();
  const [IdsSelect, setIdsSelect] = useState<number>(0);

  //API lịch sử hồ sơ
  const fetchDataGenerateDocumentDetail = async () => {
    setVisibleHandelLoad(true);
    if (ids) {
      const response = await repositorySign.get<
        MessageResponse<DetailListSetupStepModel[]>
      >(`/api/v1/SubmissionHistory/detail-approval-history?documentId=${ids}`);
      if (response && response.success) {
        setTimeout(() => {
          setVisibleHandelLoad(false);
        }, 300);

        const result = response.data;
        if (result && result.length > 0)
          setIdsSelect(result[0].setupStepId ?? 0);
        setDataHistoryProFileProps(result);
      }
    } else NotificationExtension.Info("Hồ sơ chưa được trình ký !");
    setVisibleHandelLoad(false);
  };

  useEffect(() => {
    fetchDataGenerateDocumentDetail();
  }, [ids]);

  const [listEmployee, setListEmployee] = useState<SelectListItem[]>([]);
  const [listUsers, setListUsers] = useState<UserModel[]>([]);
  const [mapDept, setMapDept] = useState<Record<string, string>>({});
  const [mapRole, setMapRole] = useState<Record<string, string>>({});

  async function getUser() {
    const responseDataEmployee = await getUsersSelect();
    if (responseDataEmployee) {
      setListEmployee(responseDataEmployee);
    }
  }

  // Lấy chi tiết người dùng + đơn vị + vai trò để map thông tin từng bước ký
  async function fetchDirectory() {
    try {
      const [usersRes, depts, roles] = await Promise.all([
        repositorySign.get<MessageResponse<UserModel[]>>(
          "/api/v1/users/get-all"
        ),
        getDepartmentSelect(),
        getRoleSelect(),
      ]);

      if (usersRes?.success) setListUsers(usersRes.data ?? []);

      const dMap: Record<string, string> = {};
      (depts ?? []).forEach((d) => {
        if (d.value !== null && d.value !== undefined)
          dMap[String(d.value)] = d.text ?? "";
      });
      setMapDept(dMap);

      const rMap: Record<string, string> = {};
      (roles ?? []).forEach((r) => {
        if (r.value !== null && r.value !== undefined)
          rMap[String(r.value)] = r.text ?? "";
      });
      setMapRole(rMap);
    } catch (error) {
      // Không chặn UI nếu directory lỗi
    }
  }

  useEffect(() => {
    getUser();
    fetchDirectory();
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (170 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getInitials = (name?: string) =>
    (name || "?")
      .trim()
      .split(/\s+/)
      .slice(-2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <>
      {dataHistoryProFileProps && (
        <>
          <LoadingOverlay
            visible={visibleHandelLoad}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
          <Grid gutter="sm">
            {/* ---------- Cột trái: Danh sách hồ sơ ---------- */}
            <Grid.Col span={{ base: 12, md: 5, lg: 4 }}>
              <Card withBorder radius="lg" shadow="sm" h={height}>
                <Card.Section p="md" bg="blue.0" withBorder>
                  <Group gap="xs">
                    <ThemeIcon variant="light" color="blue" size="md" radius="md">
                      <IconFolders size={18} />
                    </ThemeIcon>
                    <Title order={4} c="blue.7" fw={600}>
                      Danh sách hồ sơ
                    </Title>
                  </Group>
                </Card.Section>
                <ScrollArea h={height - 60} p="sm">
                  {dataHistoryProFileProps.map((value, index) => {
                    const cUser = listUsers.find(
                      (u) => String(u.id) === value.createBy?.toString()
                    );
                    const cName =
                      cUser?.name ||
                      listEmployee.find(
                        (x) => x.value === value.createBy?.toString()
                      )?.text ||
                      "—";
                    const cDept =
                      cUser?.departmentId !== undefined &&
                      cUser?.departmentId !== null
                        ? mapDept[String(cUser.departmentId)]
                        : undefined;
                    const cRole =
                      cUser?.roleId !== undefined && cUser?.roleId !== null
                        ? mapRole[String(cUser.roleId)]
                        : undefined;
                    return (
                    <Card
                      key={index}
                      onClick={() => {
                        setIdsSelect(value.setupStepId ?? 0);
                      }}
                      withBorder
                      radius="md"
                      shadow={value.setupStepId == IdsSelect ? "md" : "xs"}
                      mb="xs"
                      p="sm"
                      style={{
                        cursor: "pointer",
                        borderColor:
                          value.setupStepId == IdsSelect
                            ? "var(--mantine-color-blue-5)"
                            : "var(--mantine-color-gray-2)",
                        borderLeft:
                          value.setupStepId == IdsSelect
                            ? "3px solid var(--mantine-color-blue-6)"
                            : "3px solid transparent",
                        background:
                          value.setupStepId == IdsSelect
                            ? "var(--mantine-color-blue-0)"
                            : "white",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start">
                          <Text
                            size="sm"
                            fw={600}
                            c="blue.7"
                            style={{ flex: 1 }}
                          >
                            {value.docName}
                          </Text>
                          <Badge size="xs" variant="light" color="blue">
                            #{value.setupStepId}
                          </Badge>
                        </Group>

                        <Stack gap={4}>
                          <Group gap="xs" align="center">
                            <ActionIcon size="xs" variant="light" color="cyan">
                              <IconClock size={12} />
                            </ActionIcon>
                            <Text size="xs" c="dimmed">
                              {formatDateTransfer(value.createDate)}
                            </Text>
                          </Group>

                          <Group gap="xs" align="center" wrap="nowrap">
                            <Avatar
                              src={proxyAvatar(cUser?.avatar, 26)}
                              radius="xl"
                              size={26}
                              color="blue"
                              styles={{ image: { objectPosition: "50% 20%" } }}
                            >
                              {getInitials(cName)}
                            </Avatar>
                            <div style={{ minWidth: 0 }}>
                              <Text size="xs" c="dark" fw={600} lineClamp={1}>
                                {cName}
                              </Text>
                              {cDept && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {cDept}
                                </Text>
                              )}
                              {cRole && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {cRole}
                                </Text>
                              )}
                            </div>
                          </Group>

                          <Group justify="space-between" mt="xs">
                            <Badge variant="outline" color="green" size="xs">
                              {value.toltalQuan}/{value.approvedQuan} bước
                            </Badge>
                            <Text
                              size="xs"
                              c={
                                value.approvedQuan === value.toltalQuan
                                  ? "green"
                                  : "orange"
                              }
                              fw={500}
                            >
                              {value.approvedQuan === value.toltalQuan
                                ? "Hoàn thành"
                                : "Đang xử lý"}
                            </Text>
                          </Group>
                        </Stack>
                      </Stack>
                    </Card>
                    );
                  })}
                </ScrollArea>
              </Card>
            </Grid.Col>

            {/* ---------- Cột phải: Quá trình phê duyệt ---------- */}
            <Grid.Col span={{ base: 12, md: 7, lg: 8 }}>
              <Card withBorder radius="lg" shadow="sm" h={height}>
                <Card.Section p="md" bg="teal.0" withBorder>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <ThemeIcon
                        variant="light"
                        color="teal"
                        size="md"
                        radius="md"
                      >
                        <IconListCheck size={18} />
                      </ThemeIcon>
                      <Title order={4} c="teal.7" fw={600}>
                        Quá trình phê duyệt
                      </Title>
                    </Group>
                    {IdsSelect ? (
                      <Badge variant="light" color="teal" size="lg" radius="sm">
                        #{IdsSelect}
                      </Badge>
                    ) : null}
                  </Group>
                </Card.Section>
                <ScrollArea h={height - 60} p="sm">
                  <Stack gap={0}>
                    {dataHistoryProFileProps
                      ?.find((value) => value.setupStepId === IdsSelect)
                      ?.detailApproveHistoryModels?.map((value, index) => {
                        const isApproved = value.status === "PHE_DUYET";
                        const isRejected = value.status === "TU_CHOI";
                        const isInProgress = !isApproved && !isRejected;
                        const _val =
                          dataHistoryProFileProps?.find(
                            (v) => v.setupStepId === IdsSelect
                          )?.detailApproveHistoryModels ?? [];
                        const isLastStep = value.stepNumber === _val.length;

                        let statusText;
                        let colorLastStep = "";

                        if (isLastStep) {
                          if (isApproved) {
                            statusText = "Hoàn thành";
                            colorLastStep = "green";
                          } else if (isRejected) {
                            statusText = "Từ chối";
                            colorLastStep = "red";
                          } else if (isRejected || isInProgress) {
                            statusText = "Chưa hoàn thành";
                            colorLastStep = "gray";
                          } else {
                            statusText = "Hoàn thành";
                            colorLastStep = "green";
                          }
                        } else {
                          if (isApproved) {
                            statusText = "Đã duyệt";
                            colorLastStep = "green";
                          } else if (isRejected) {
                            statusText = "Từ chối";
                            colorLastStep = "red";
                          } else {
                            statusText = "Đã yêu cầu";
                            colorLastStep = "orange";
                          }
                        }

                        const showDateDivider =
                          formatDateTransferVN(
                            _val[index - 1]?.processingDate
                          ) !== formatDateTransferVN(value.processingDate) &&
                          value.processingDate;

                        // Map thông tin người ký từ /users/get-all + đơn vị + vai trò
                        const user = listUsers.find(
                          (u) => String(u.id) === value.empId
                        );
                        const signerName =
                          user?.name ||
                          listEmployee.find((x) => x.value === value.empId)
                            ?.text ||
                          value.empId ||
                          "Chưa xác định";
                        const deptName =
                          user?.departmentId !== undefined &&
                          user?.departmentId !== null
                            ? mapDept[String(user.departmentId)]
                            : undefined;
                        const roleName =
                          user?.roleId !== undefined && user?.roleId !== null
                            ? mapRole[String(user.roleId)]
                            : undefined;
                        const nodeColor = isApproved
                          ? "green"
                          : isRejected
                          ? "red"
                          : "orange";
                        const time = value.processingDate
                          ? formatDateTransfer(value.processingDate)
                              ?.split(" ")[1]
                              ?.split(":")
                              ?.slice(0, 2)
                              ?.join(":")
                          : "";

                        const prevApproved =
                          _val[index - 1]?.status === "PHE_DUYET";
                        const lineColorDown = isApproved
                          ? "var(--mantine-color-green-5)"
                          : "var(--mantine-color-gray-3)";
                        const lineColorInto = prevApproved
                          ? "var(--mantine-color-green-5)"
                          : "var(--mantine-color-gray-3)";

                        return (
                          <React.Fragment key={index}>
                            {showDateDivider && (
                              <Group align="stretch" gap="sm" wrap="nowrap">
                                <Box
                                  style={{
                                    flex: "0 0 40px",
                                    width: 40,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  {index > 0 && (
                                    <Box
                                      style={{
                                        width: 2,
                                        flex: 1,
                                        background: lineColorInto,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box
                                  style={{
                                    flex: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    paddingTop: 2,
                                    paddingBottom: 10,
                                  }}
                                >
                                  <Badge
                                    variant="light"
                                    color="gray"
                                    radius="sm"
                                    leftSection={<IconCalendar size={12} />}
                                    styles={{ label: { fontWeight: 600 } }}
                                  >
                                    Ngày{" "}
                                    {formatDateTransferVN(
                                      value.processingDate ?? ""
                                    )}
                                  </Badge>
                                </Box>
                              </Group>
                            )}

                            <Group align="stretch" gap="sm" wrap="nowrap">
                              {/* Trục timeline liền mạch */}
                              <Box
                                style={{
                                  flex: "0 0 40px",
                                  width: 40,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <ActionIcon
                                  size="lg"
                                  variant="filled"
                                  color={nodeColor}
                                  radius="xl"
                                >
                                  {isApproved ? (
                                    <IconThumbUpFilled size={16} />
                                  ) : isRejected ? (
                                    <IconCircleXFilled size={16} />
                                  ) : (
                                    <IconClockFilled size={16} />
                                  )}
                                </ActionIcon>
                                {!isLastStep && (
                                  <Box
                                    style={{
                                      width: 2,
                                      flex: 1,
                                      minHeight: 10,
                                      marginTop: 2,
                                      background: lineColorDown,
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Nội dung bước ký */}
                              <Box style={{ flex: 1, paddingBottom: 12 }}>
                              <Card
                                withBorder
                                radius="md"
                                p="sm"
                                shadow="xs"
                                style={{
                                  flex: 1,
                                  background: isApproved
                                    ? "var(--mantine-color-green-0)"
                                    : isRejected
                                    ? "var(--mantine-color-red-0)"
                                    : "var(--mantine-color-orange-0)",
                                  borderColor: isApproved
                                    ? "var(--mantine-color-green-4)"
                                    : isRejected
                                    ? "var(--mantine-color-red-4)"
                                    : "var(--mantine-color-orange-4)",
                                  borderLeft: `3px solid ${
                                    isApproved
                                      ? "var(--mantine-color-green-6)"
                                      : isRejected
                                      ? "var(--mantine-color-red-6)"
                                      : "var(--mantine-color-orange-6)"
                                  }`,
                                }}
                              >
                                <Grid align="center">
                                  <Grid.Col span={{ base: 12, md: 8 }}>
                                    <Group
                                      gap="sm"
                                      align="flex-start"
                                      wrap="nowrap"
                                    >
                                      <Avatar
                                        src={proxyAvatar(user?.avatar, 48)}
                                        radius="xl"
                                        size={48}
                                        color={nodeColor}
                                        styles={{
                                          image: { objectPosition: "50% 20%" },
                                        }}
                                      >
                                        {getInitials(signerName?.toString())}
                                      </Avatar>

                                      <Stack gap={4} style={{ flex: 1 }}>
                                        <Group gap="xs">
                                          <Badge
                                            variant="filled"
                                            color="blue"
                                            size="xs"
                                          >
                                            Bước {value.stepNumber}
                                          </Badge>
                                          <Text fw={600} size="sm">
                                            {value.stepName}
                                          </Text>
                                        </Group>

                                        <Text fw={700} size="sm" c="dark">
                                          {signerName}
                                        </Text>

                                        {user?.email && (
                                          <Group gap={6} wrap="nowrap">
                                            <IconMail
                                              size={13}
                                              color="var(--mantine-color-gray-6)"
                                            />
                                            <Text
                                              size="xs"
                                              c="dimmed"
                                              lineClamp={1}
                                            >
                                              {user.email}
                                            </Text>
                                          </Group>
                                        )}
                                        {user?.phone && (
                                          <Group gap={6} wrap="nowrap">
                                            <IconPhone
                                              size={13}
                                              color="var(--mantine-color-gray-6)"
                                            />
                                            <Text size="xs" c="dimmed">
                                              {user.phone}
                                            </Text>
                                          </Group>
                                        )}
                                        {deptName && (
                                          <Group gap={6} wrap="nowrap">
                                            <IconBuilding
                                              size={13}
                                              color="var(--mantine-color-gray-6)"
                                            />
                                            <Text
                                              size="xs"
                                              c="dimmed"
                                              lineClamp={1}
                                            >
                                              {deptName}
                                            </Text>
                                          </Group>
                                        )}
                                        {roleName && (
                                          <Group gap={6} wrap="nowrap">
                                            <IconBadge
                                              size={13}
                                              color="var(--mantine-color-gray-6)"
                                            />
                                            <Text
                                              size="xs"
                                              c="dimmed"
                                              lineClamp={1}
                                            >
                                              {roleName}
                                            </Text>
                                          </Group>
                                        )}

                                        <Group gap="md" align="center" mt={2}>
                                          <Text size="xs" fw={600} c="dark">
                                            Loại ký:{" "}
                                            {convertSignType(value.signType ?? 0)}
                                          </Text>
                                          {value.processingDate && (
                                            <Group gap="xs">
                                              <Text size="xs" c="dimmed">
                                                Thời gian:
                                              </Text>
                                              <Badge
                                                variant="outline"
                                                color="blue"
                                                size="xs"
                                              >
                                                {time}
                                              </Badge>
                                            </Group>
                                          )}
                                        </Group>
                                      </Stack>
                                    </Group>
                                  </Grid.Col>

                                  <Grid.Col span={{ base: 12, md: 4 }}>
                                    <Stack gap="xs" align="stretch">
                                      <Text
                                        fw={500}
                                        size="xs"
                                        c="gray.6"
                                        ta="center"
                                      >
                                        Tiến trình
                                      </Text>

                                      <Stack gap="xs" align="center">
                                        <Button
                                          variant="light"
                                          size="xs"
                                          color="blue"
                                          leftSection={
                                            <IconClockFilled size={12} />
                                          }
                                          radius="xl"
                                          fullWidth
                                          styles={{
                                            root: {
                                              fontWeight: 500,
                                              maxWidth: "140px",
                                            },
                                          }}
                                        >
                                          Yêu cầu
                                        </Button>

                                        <IconArrowRight
                                          size={12}
                                          color="var(--mantine-color-blue-6)"
                                        />

                                        <Button
                                          variant="filled"
                                          size="xs"
                                          color={colorLastStep}
                                          leftSection={
                                            isLastStep ? (
                                              isRejected || isInProgress ? (
                                                <IconClockFilled size={12} />
                                              ) : (
                                                <IconCircleCheckFilled
                                                  size={12}
                                                />
                                              )
                                            ) : isApproved ? (
                                              <IconThumbUpFilled size={12} />
                                            ) : isRejected ? (
                                              <IconCircleXFilled size={12} />
                                            ) : (
                                              <IconClockFilled size={12} />
                                            )
                                          }
                                          radius="xl"
                                          fullWidth
                                          styles={{
                                            root: {
                                              fontWeight: 600,
                                              maxWidth: "140px",
                                              fontSize: "11px",
                                            },
                                          }}
                                        >
                                          {statusText}
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </Grid.Col>
                                </Grid>
                              </Card>
                              </Box>
                            </Group>
                          </React.Fragment>
                        );
                      })}
                  </Stack>
                </ScrollArea>
              </Card>
            </Grid.Col>
          </Grid>
        </>
      )}

      {!dataHistoryProFileProps && <Loading />}
    </>
  );
};

export default HistoryProfile;
